import apiFetch from './auth/Auth';

// Interfaces
export interface CompanySettingsVM {
  companySettingsIdPK?: number;
  currencyCode: string;
  currencyName: string;
  currencySymbol: string;
  currencySymbolPosition: string;
  decimalPlaces: number;
  decimalSeparator: string;
  thousandSeparator: string;
  timeZoneId: string;
  timeZoneName: string;
  timeZoneOffset: number;
  dateFormat: string;
  timeFormat: string;
  dateTimeFormat: string;
  numberFormat: string;
  languageCode: string;
  countryCode?: string;
  fiscalYearStartMonth: number;
  fiscalYearStartDay: number;
  weekStartDay: string;
  defaultTaxRate?: number;
  taxLabel: string;
  isTaxInclusive: boolean;
  showCurrencySymbol: boolean;
  showThousandSeparator: boolean;
  customSettings?: Record<string, any>;
}

export interface CurrencyInfoVM {
  code: string;
  name: string;
  symbol: string;
}

export interface TimeZoneInfoVM {
  id: string;
  displayName: string;
  offsetMinutes: number;
  offsetDisplay: string;
}

export interface CompanyCustomSettingsVM {
  companyCustomSettingsIdPK?: number;
  settingKey: string;
  settingValue: string;
  description?: string;
  category?: string;
  dataType: string;
  isSystemSetting: boolean;
}

export interface SaveCustomSettingVM {
  settingKey: string;
  settingValue: string;
  description?: string;
  category?: string;
  dataType?: string;
}

export interface BulkSaveCustomSettingsVM {
  settings: SaveCustomSettingVM[];
}

export interface FormattedValueVM {
  originalValue: number;
  formattedValue: string;
  currencyCode: string;
  currencySymbol: string;
}

// API Functions

/**
 * Get company settings
 */
export const getCompanySettings = async () => {
  try {
    const response = await apiFetch('CompanySettings', {
      method: 'GET',
    });
    return response;
  } catch (error) {
    console.error('Failed to fetch company settings:', error);
    throw error;
  }
};

/**
 * Save or update company settings
 */
export const saveCompanySettings = async (settings: CompanySettingsVM) => {
  try {
    const response = await apiFetch('CompanySettings', {
      method: 'POST',
      body: JSON.stringify(settings),
    });
    return response;
  } catch (error) {
    console.error('Failed to save company settings:', error);
    throw error;
  }
};

/**
 * Get list of supported currencies
 */
export const getSupportedCurrencies = async (): Promise<{ currencies: CurrencyInfoVM[] }> => {
  try {
    const response = await apiFetch('CompanySettings/currencies', {
      method: 'GET',
    });
    return response;
  } catch (error) {
    console.error('Failed to fetch currencies:', error);
    throw error;
  }
};

/**
 * Get list of time zones
 */
export const getTimeZones = async (): Promise<{ timeZones: TimeZoneInfoVM[] }> => {
  try {
    const response = await apiFetch('CompanySettings/timezones', {
      method: 'GET',
    });
    return response;
  } catch (error) {
    console.error('Failed to fetch time zones:', error);
    throw error;
  }
};

/**
 * Format a number according to company settings
 */
export const formatCurrency = async (value: number): Promise<FormattedValueVM> => {
  try {
    const response = await apiFetch('CompanySettings/format-currency', {
      method: 'POST',
      body: JSON.stringify(value),
    });
    return response;
  } catch (error) {
    console.error('Failed to format currency:', error);
    throw error;
  }
};

/**
 * Convert UTC time to company timezone
 */
export const convertToCompanyTime = async (utcTime: Date) => {
  try {
    const response = await apiFetch('CompanySettings/convert-time', {
      method: 'POST',
      body: JSON.stringify(utcTime.toISOString()),
    });
    return response;
  } catch (error) {
    console.error('Failed to convert time:', error);
    throw error;
  }
};

/**
 * Get current time in company timezone
 */
export const getCurrentCompanyTime = async () => {
  try {
    const response = await apiFetch('CompanySettings/current-time', {
      method: 'GET',
    });
    return response;
  } catch (error) {
    console.error('Failed to get current company time:', error);
    throw error;
  }
};

/**
 * Get all custom settings (optionally by category)
 */
export const getCustomSettings = async (category?: string) => {
  try {
    const url = category 
      ? `CompanySettings/custom-settings?category=${encodeURIComponent(category)}`
      : 'CompanySettings/custom-settings';
    
    const response = await apiFetch(url, {
      method: 'GET',
    });
    return response;
  } catch (error) {
    console.error('Failed to fetch custom settings:', error);
    throw error;
  }
};

/**
 * Get a specific custom setting by key
 */
export const getCustomSettingByKey = async (settingKey: string) => {
  try {
    const response = await apiFetch(`CompanySettings/custom-settings/${encodeURIComponent(settingKey)}`, {
      method: 'GET',
    });
    return response;
  } catch (error) {
    console.error('Failed to fetch custom setting:', error);
    throw error;
  }
};

/**
 * Save or update a single custom setting
 */
export const saveCustomSetting = async (setting: SaveCustomSettingVM) => {
  try {
    const response = await apiFetch('CompanySettings/custom-settings', {
      method: 'POST',
      body: JSON.stringify(setting),
    });
    return response;
  } catch (error) {
    console.error('Failed to save custom setting:', error);
    throw error;
  }
};

/**
 * Save or update multiple custom settings at once
 */
export const saveCustomSettingsBulk = async (settings: SaveCustomSettingVM[]) => {
  try {
    const response = await apiFetch('CompanySettings/custom-settings/bulk', {
      method: 'POST',
      body: JSON.stringify({ settings }),
    });
    return response;
  } catch (error) {
    console.error('Failed to save custom settings in bulk:', error);
    throw error;
  }
};

/**
 * Delete a custom setting
 */
export const deleteCustomSetting = async (settingKey: string) => {
  try {
    const response = await apiFetch(`CompanySettings/custom-settings/${encodeURIComponent(settingKey)}`, {
      method: 'DELETE',
    });
    return response;
  } catch (error) {
    console.error('Failed to delete custom setting:', error);
    throw error;
  }
};

/**
 * Initialize default POS settings
 */
export const initializeDefaultPOSSettings = async () => {
  try {
    const response = await apiFetch('CompanySettings/custom-settings/initialize-pos', {
      method: 'POST',
    });
    return response;
  } catch (error) {
    console.error('Failed to initialize POS settings:', error);
    throw error;
  }
};

// Helper function to parse setting values based on dataType
export const parseSettingValue = (settingValue: string, dataType: string): any => {
  if (!settingValue) {
    return null;
  }

  try {
    switch (dataType?.toLowerCase()) {
      case 'boolean':
      case 'bool':
        return settingValue.toLowerCase() === 'true';

      case 'number':
      case 'int':
      case 'integer':
        return parseInt(settingValue, 10);

      case 'decimal':
      case 'double':
        return parseFloat(settingValue);

      case 'json':
      case 'object':
        return JSON.parse(settingValue);

      case 'array':
        return JSON.parse(settingValue);

      case 'string':
      default:
        return settingValue;
    }
  } catch {
    return settingValue; // Return as string if parsing fails
  }
};

// Helper function to format setting values for saving
export const formatSettingValue = (value: any): string => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
};

// Invoice Template Interfaces
export interface InvoiceTemplateVM {
  invoiceTemplateIdPK?: number;
  companyId?: number;
  templateName: string;
  templateType: string;
  designFormat?: string; // 'Receipt' or 'Invoice'
  isDefault: boolean;
  showQRCode: boolean;
  showBillNumber: boolean;
  showCustomerName: boolean;
  showCustomerAddress: boolean;
  showCustomerPhone: boolean;
  showCustomerEmail: boolean;
  showItemDescription: boolean;
  showItemBarcode: boolean;
  showItemDiscount: boolean;
  showItemTax: boolean;
  showOrderDiscount: boolean;
  showOrderTax: boolean;
  showPaymentMethod: boolean;
  showCashierName: boolean;
  showBranchName: boolean;
  showBranchAddress: boolean;
  showBranchPhone: boolean;
  showCompanyLogo: boolean;
  showCompanyName: boolean;
  showCompanyAddress: boolean;
  showCompanyPhone: boolean;
  showCompanyEmail: boolean;
  showCompanyWebsite: boolean;
  showTaxIdentificationNumber: boolean;
  showDate: boolean;
  showTime: boolean;
  footerText?: string;
  showFooterText: boolean;
  termsAndConditions?: string;
  showTermsAndConditions: boolean;
  qrCodeType?: string;
  qrCodeData?: string;
  thermalPaperWidth?: number;
  thermalAutoCut: boolean;
  thermalOpenCashDrawer: boolean;
  alignment?: string;
  fontSize?: number;
  fontFamily?: string;
  additionalSettings?: string;
  isActive: boolean;
  // ZATCA / Saudi Arabia e-Invoice settings
  showArabicTranslation?: boolean;
  showZatcaQR?: boolean;
  vatRegistrationNumber?: string;
}

// Invoice Template API Functions

/**
 * Get all invoice templates
 */
export const getInvoiceTemplates = async (): Promise<{ templates: InvoiceTemplateVM[] }> => {
  try {
    const response = await apiFetch('CompanySettings/invoice-templates', {
      method: 'GET',
    });
    return response;
  } catch (error) {
    console.error('Failed to fetch invoice templates:', error);
    throw error;
  }
};

/**
 * Get default invoice template
 */
export const getDefaultInvoiceTemplate = async (): Promise<{ template: InvoiceTemplateVM; isDefault: boolean }> => {
  try {
    const response = await apiFetch('CompanySettings/invoice-templates/default', {
      method: 'GET',
    });
    return response;
  } catch (error) {
    console.error('Failed to fetch default invoice template:', error);
    throw error;
  }
};

/**
 * Get invoice template by ID
 */
export const getInvoiceTemplateById = async (id: number): Promise<{ template: InvoiceTemplateVM }> => {
  try {
    const response = await apiFetch(`CompanySettings/invoice-templates/${id}`, {
      method: 'GET',
    });
    return response;
  } catch (error) {
    console.error('Failed to fetch invoice template:', error);
    throw error;
  }
};

/**
 * Save or update invoice template
 */
export const saveInvoiceTemplate = async (template: InvoiceTemplateVM): Promise<{ message: string; template: InvoiceTemplateVM }> => {
  try {
    const response = await apiFetch('CompanySettings/invoice-templates', {
      method: 'POST',
      body: JSON.stringify(template),
    });
    return response;
  } catch (error) {
    console.error('Failed to save invoice template:', error);
    throw error;
  }
};

/**
 * Set invoice template as default
 */
export const setDefaultInvoiceTemplate = async (id: number): Promise<{ message: string; template: InvoiceTemplateVM }> => {
  try {
    const response = await apiFetch(`CompanySettings/invoice-templates/${id}/set-default`, {
      method: 'POST',
    });
    return response;
  } catch (error) {
    console.error('Failed to set default invoice template:', error);
    throw error;
  }
};

/**
 * Delete invoice template
 */
export const deleteInvoiceTemplate = async (id: number): Promise<{ message: string }> => {
  try {
    const response = await apiFetch(`CompanySettings/invoice-templates/${id}`, {
      method: 'DELETE',
    });
    return response;
  } catch (error) {
    console.error('Failed to delete invoice template:', error);
    throw error;
  }
};


